import { useCallback, useEffect, useState } from 'react';

import { useAddToast } from '../../contexts/ToastContext';
import { useLocalStorage } from '../../helpers/localStorage';
import { usePollJobs } from '../../hooks/api';

import { failureMessages, successMessages } from './consts';
import type { JobsStore } from './types';

const JOBS_LOCAL_STORAGE_KEY = '__FOSSA__jobs';

/**
 * Hook which powers the JobNotificationProvider. This hook stores a queue of jobs
 * which the user has requested to be notified about. If there are jobs in the queue,
 * it will poll the API for updates on the job status. If any of the returned jobs are
 * marked as either finished or failed, a toast will be displayed and those jobs will
 * be removed from the queue. Note that it does not poll if there are no jobs in the queue.
 * This state is backed up to localStorage, so that jobs will still be polled for if the user
 * reloads or closes the page.
 *
 * Returns the onAddJob function, as that it all the Provider needs to expose to the app,
 * because all the state and logic is handled here.
 *
 * DO NOT use this directly (it should only be used by the Provider since it is stateful).
 * Instead use the useAddJobToNotificationQueue hook, which provides the onAddJob function.
 *
 * @returns {AddJobFn} onAddJob - A function which takes the jobToken and jobName and adds it to the queue.
 */
const useJobNotificationHub = () => {
  const ls = useLocalStorage<JobsStore>(JOBS_LOCAL_STORAGE_KEY);
  const addToast = useAddToast();
  //  Load the jobs from local storage. If there are none, default to an empty object.
  const [jobs, setJobs] = useState<JobsStore>(ls.get() ?? {});

  //  Derive the list of tokens, which are used as the keys for the jobs object.
  const jobTokens = Object.keys(jobs);
  //  Only enable polling if there are jobs to poll for. React Query will not fetch if enabled is false.
  const shouldFetch = jobTokens.length > 0;
  //  Fetch the jobs by their tokens.
  const { data: fetchedJobs, isLoading, isError } = usePollJobs(jobTokens, shouldFetch);

  //  Handle the success and failure messages for the toasts.
  const onAddSuccessToast = useCallback((jobToken: string) => {
    const jobName = jobs[jobToken]?.name ?? '';
    const content = successMessages[jobName] ?? `Job "${jobToken}" finished.`;
    addToast({ type: 'success', content });
  }, [addToast, jobs]);

  const onAddFailureToast = useCallback((jobToken: string) => {
    const jobName = jobs[jobToken]?.name ?? '';
    const content = failureMessages[jobName] ?? `Job "${jobToken}" failed.`;
    addToast({ type: 'error', content });
  }, [addToast, jobs]);

  //  Effect where we check the if any of the returned jobs are in our queue.
  //  If they are, we check if they are finished or failed and display a toast.
  //  After they are toasted, we remove them from the queue.
  useEffect(() => {
    //  "Any time the fetched jobs data changes and the data is not loading"
    if (fetchedJobs && !isError && !isLoading) {
      const existingJobs = new Set(jobTokens);
      //  List of job tokens to remove.
      const jobsToRemove = new Set();

      //  Update the user with a toast if their job has finished or failed.
      Object.entries(fetchedJobs.jobs).forEach(([token, job]) => {
        if (existingJobs.has(token)) {
          if (job.status === 'finished') {
            onAddSuccessToast(token);
            jobsToRemove.add(token);
          } else if (job.status === 'failed') {
            onAddFailureToast(token);
            jobsToRemove.add(token);
          }
        }
      });

      //  If any jobTokens are missing from the response, remove those from the store as well.
      //  This is mostly for developer sanity, as it can happen if you are connected to a different environment (like staging),
      //  and then switch back to your local environment.
      const fetchedJobsTokens = new Set(Object.keys(fetchedJobs.jobs));
      const missingJobs = jobTokens.filter((token) => !fetchedJobsTokens.has(token));
      //  Add them to the list of jobs to remove.
      missingJobs.forEach((token) => jobsToRemove.add(token));

      //  If we have any jobs which need to be removed from the queue, do so.
      if (jobsToRemove.size > 0) {
        setJobs((prevJobs) => {
          const newJobs = Object.fromEntries(
            Object.entries(prevJobs)
              .filter(([token]) => !jobsToRemove.has(token)),
          );
          //  Set the job in localStorage
          ls.set(newJobs);
          return newJobs;
        });
      }
    }
  }, [ls, fetchedJobs, isError, isLoading, jobTokens, onAddSuccessToast, onAddFailureToast]);

  /**
   * Adds a new job to the queue be polled for. This also sets it in localStorage,
   * so that if the user was to reload the page, the job would still be in the queue.
   */
  const onAddJob = useCallback((jobToken: string, jobName: string) => setJobs((prevJobs) => {
    const newJobs: JobsStore = {
      ...prevJobs,
      [jobToken]: { name: jobName },
    };
    //  Store the jobs in localStorage
    ls.set(newJobs);
    //  Set the state here
    return newJobs;
  }), [ls]);

  return onAddJob;
};

export default useJobNotificationHub;
